import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHourglassHalf} from "react-icons/fa6";

import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Code,
  Divider,
  Card,
  CardBody,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Flex,
  Wrap,
  WrapItem,
  IconButton,
  Tooltip,
  Image,
  List,
  ListItem,
  ListIcon,
  useToast
} from '@chakra-ui/react';
import { 
  FaServer,
  FaCloud,
  FaPlay,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaChartLine,
  FaMicrochip,
  FaSignOutAlt,
  FaTimes,
  FaSyncAlt,
  FaSearch,
  FaArrowLeft,
  FaCalendarAlt,
  FaFileAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
  FaShoppingCart,
  FaStop, // For stop icon  
  FaBan,     // For ban/prohibition icon
  // FaFilm, // Viewer button disabled
  FaCopy, // Add this for copy icon
  FaBrain, // Add this for AI diagnosis icon
  FaListAlt, // Add this for KPI list icon
  FaCog,
  FaRocket
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
// import copilotApiService from '../services/copilotApi';
import { getProductDisplayName } from '../utils/productNames';
import { MdFunctions } from "react-icons/md";


import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const Jobs = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingJobs, setDownloadingJobs] = useState([]);
  const [cancellingJobs, setCancellingJobs] = useState([]);
  const [deletingJobs, setDeletingJobs] = useState([]);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [toolFilter, setToolFilter] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(10);

  // Subscription states
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  // Consumed hours states
  const [consumedHours, setConsumedHours] = useState(null);
  const [consumedHoursLoading, setConsumedHoursLoading] = useState(true);
  const [consumedHoursError, setConsumedHoursError] = useState(null);

  // Purchase summary states
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [purchaseSummaryLoading, setPurchaseSummaryLoading] = useState(true);
  const [purchaseSummaryError, setPurchaseSummaryError] = useState(null);

  // Copilot balance states — temporarily hidden
  // const [copilotBalance, setCopilotBalance] = useState(null);
  // const [copilotBalanceLoading, setCopilotBalanceLoading] = useState(true);

  // Modal state for error dialog
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedError, setSelectedError] = useState({ jobId: '', error: '', jobName: '' });
  
  // Diagnosis state
  const [diagnosisData, setDiagnosisData] = useState(null);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);

  // Job eligibility states
  const [jobEligibility, setJobEligibility] = useState(null);
  const [jobEligibilityLoading, setJobEligibilityLoading] = useState(true);

  // Add confirmation modal states
  const { 
    isOpen: isErrorModalOpen, 
    onOpen: onErrorModalOpen, 
    onClose: onErrorModalClose 
  } = useDisclosure();
  const { 
    isOpen: isConfirmModalOpen, 
    onOpen: onConfirmModalOpen, 
    onClose: onConfirmModalClose 
  } = useDisclosure();
  const { 
    isOpen: isDeleteModalOpen, 
    onOpen: onDeleteModalOpen, 
    onClose: onDeleteModalClose 
  } = useDisclosure();
  const [jobToCancel, setJobToCancel] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);

  // Add job logs modal states
  const { 
    isOpen: isJobLogsModalOpen, 
    onOpen: onJobLogsModalOpen, 
    onClose: onJobLogsModalClose 
  } = useDisclosure();
  const [selectedJobLogs, setSelectedJobLogs] = useState({ 
    jobId: '', 
    logs: '', 
    jobName: '', 
    status: '' 
  });
  const [jobLogsLoading, setJobLogsLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false); // New state for copy success
  const [fixedCodeCopySuccess, setFixedCodeCopySuccess] = useState(false); // Add this new state
  
  const [estimatedTimes, setEstimatedTimes] = useState({});
  const [estimatedTimesLoading, setEstimatedTimesLoading] = useState({});
  
  
  // Color mode values
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const logsContainerRef = useRef(null);

  useEffect(() => {
    fetchJobs();
    fetchJobEligibility();
    fetchSubscriptionDetails();
    fetchConsumedHours();
    fetchPurchaseSummary();
    // fetchCopilotBalance();
  }, []);

  // Add new useEffect to fetch estimated times when jobs change
  useEffect(() => {
    if (jobs.length > 0) {
      fetchEstimatedTimesForActiveJobs();
    }
  }, [jobs]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [jobs, statusFilter, searchQuery, toolFilter]);

  // Add the fetchConsumedHours function
  const fetchConsumedHours = async () => {
    try {
      setConsumedHoursLoading(true);
      setConsumedHoursError(null);
      const hours = await apiService.getUserConsumedHours();
      setConsumedHours(hours);
    } catch (err) {
      console.error('Failed to fetch consumed hours:', err);
      setConsumedHoursError('Failed to load usage statistics');
    } finally {
      setConsumedHoursLoading(false);
    }
  };

  // Add the fetchPurchaseSummary function
  const fetchPurchaseSummary = async () => {
    try {
      setPurchaseSummaryLoading(true);
      setPurchaseSummaryError(null);
      const summary = await apiService.getPurchaseSummary();
      setPurchaseSummary(summary);
    } catch (err) {
      console.error('Failed to fetch purchase summary:', err);
      setPurchaseSummaryError('Failed to load purchase summary');
    } finally {
      setPurchaseSummaryLoading(false);
    }
  };

  // Fetch Copilot balance — temporarily hidden
  /*
  const fetchCopilotBalance = async () => {
    try {
      setCopilotBalanceLoading(true);
      const data = await copilotApiService.getBalance();
      setCopilotBalance(data);
    } catch (err) {
      console.error('Failed to fetch Copilot balance:', err);
      setCopilotBalance({ has_credits: false });
    } finally {
      setCopilotBalanceLoading(false);
    }
  };
  */

  // Helper function to format hours
  const formatHours = (hours) => {
    if (!hours && hours !== 0) return 'N/A';
    /*if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    }*/
    return `${hours.toFixed(1)}h`;
  };

  // Helper function to format date from ISO string
  const formatEndDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Add the fetchSubscriptionDetails function
  const fetchSubscriptionDetails = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      const details = await apiService.getSubscriptionDetails();
      setSubscriptionDetails(details);
    } catch (err) {
      console.error('Failed to fetch subscription details:', err);
      setSubscriptionError('Failed to load subscription details');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Helper functions for subscription display
  const getSubscriptionStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'inactive':
      case 'cancelled':
        return 'red';
      case 'trial':
        return 'blue';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const jobsData = await apiService.getUserJobs();
      // Sort by enqueued_at date (newest first)

      
      const sortedJobs = jobsData.sort((a, b) => new Date(b.enqueued_at || 0) - new Date(a.enqueued_at || 0));
      setJobs(sortedJobs);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobEligibility = async () => {
    try {
      setJobEligibilityLoading(true);
      const eligibility = await apiService.checkJobEligibility();
      
      // Handle the new API response format
      if (eligibility && typeof eligibility === 'object') {
        setJobEligibility({
          is_eligible: eligibility.is_eligible || false,
          remaining_hours: eligibility.remaining_hours || 0,
          reason: eligibility.reason || 'Unknown eligibility status'
        });
      } else {
        // Fallback for unexpected response format
        setJobEligibility({
          is_eligible: false,
          remaining_hours: 0,
          reason: 'Invalid response format'
        });
      }
    } catch (err) {
      console.error('Failed to fetch job eligibility:', err);
      setJobEligibility({
        is_eligible: false,
        remaining_hours: 0,
        reason: 'Failed to check eligibility'
      });
    } finally {
      setJobEligibilityLoading(false);
    }
  };



    const fetchJobEstimatedTime = async (jobId) => {
      try {
        setEstimatedTimesLoading(prev => ({ ...prev, [jobId]: true }));
        const estimatedTime = await apiService.getJobEstimatedTime(jobId);
        setEstimatedTimes(prev => ({ ...prev, [jobId]: estimatedTime }));
      } catch (error) {
        console.error(`Failed to fetch estimated time for job ${jobId}:`, error);
        // Set a default error state
        setEstimatedTimes(prev => ({ 
          ...prev, 
          [jobId]: { message: 'Unable to fetch estimated time' } 
        }));
      } finally {
        setEstimatedTimesLoading(prev => ({ ...prev, [jobId]: false }));
      }
    };
  
    // Fetch estimated times for running/preprocessing jobs
    const fetchEstimatedTimesForActiveJobs = async () => {
      const activeJobs = jobs.filter(job => 
        job.status?.toLowerCase() === 'running' || 
        job.status?.toLowerCase() === 'preprocessing'
      );
      
      for (const job of activeJobs) {
        await fetchJobEstimatedTime(job.job_id);
      }
    };

  // Add comprehensive refresh function
  const handleRefreshAll = async () => {
    setLoading(true);
    try {
      // Refresh all data in parallel for better performance
      await Promise.all([
        fetchJobs(),
        fetchJobEligibility(),
        fetchConsumedHours(),
        fetchSubscriptionDetails(),
        fetchPurchaseSummary()
      ]);
      // Fetch estimated times for active jobs
      await fetchEstimatedTimesForActiveJobs();
    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = jobs;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status?.toLowerCase() === statusFilter);
    }

    // Tool filter
    if (toolFilter !== 'all') {
      filtered = filtered.filter(job => job.tool_name === toolFilter);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(job => 
        job.job_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.job_desc?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.job_id?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'running':
        return 'blue';
      case 'preprocessing':
        return 'purple';
      case 'enqueue':
        return 'yellow';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'gray';
      case 'deleted':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return FaCheckCircle;
      case 'running':
        return FaClock;
      case 'preprocessing':
        return FaCog;
      case 'enqueue':
        return FaHourglassHalf;
      case 'failed':
        return FaExclamationTriangle;
      case 'cancelled':
        return FaBan;
      case 'deleted':
        return FaTrash;
      default:
        return FaClock;
    }
  };

  // Add after the formatDuration function
  const formatEstimatedTime = (seconds) => {
    if (!seconds || seconds === '0') return 'N/A';
    const secs = parseInt(seconds);
    if (isNaN(secs)) return 'N/A';
    
    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatSubscriptionDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {

    console.log(seconds);
    if (!seconds) return `0m`;
    if (seconds===0) return `0m`;
    if (seconds<60) return `0m`;


    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }

    if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `0m`;
    }
        
  };

  const formatFileSize = (sizeInMB) => {
    if (!sizeInMB) return 'N/A';
    // Size is already in MB, just format it nicely
    return `${sizeInMB.toFixed(1)} MB`;
  };

  const handleDownload = async (jobId) => {
    try {
      setDownloadingJobs((prev) => [...prev, jobId]);
  
      // Step 1: ask backend for signed URL
      const response = await fetch(`${apiService.baseURL}/get-download-link/${jobId}`, {
        headers: apiService.getHeaders(true), // include auth
      });
      if (!response.ok) throw new Error("Failed to get download link");
      const { download_url } = await response.json();
  
      // Step 2: redirect browser to signed URL (streams large file)
      window.open(`${apiService.baseURL}${download_url}`, "_blank");
    } catch (error) {
      console.error("Download failed:", error);
      toast({ title: 'Download failed', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setDownloadingJobs((prev) => prev.filter((id) => id !== jobId));
    }
  };


  const handleDownloadBlob = async (jobId) => {
    try {
      setDownloadingJobs((prev) => [...prev, jobId]);
  
      const { blobUrl } = await apiService.downloadZipFile(jobId);
  
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `job_${jobId}_results.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error("Download failed:", error);
      toast({ title: 'Download failed', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setDownloadingJobs((prev) => prev.filter((id) => id !== jobId));
    }
  };

  const handleCancel = async (jobId) => {
    try {
      setCancellingJobs(prev => [...prev, jobId]);
      await apiService.cancelJob(jobId);
      fetchJobs(); // Refresh the job list
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast({ title: 'Failed to cancel job', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setCancellingJobs(prev => prev.filter(id => id !== jobId));
    }
  };

  const handleCancelClick = (job) => {
    setJobToCancel(job);
    onConfirmModalOpen();
  };

  const handleConfirmCancel = async () => {
    if (!jobToCancel) return;
    
    try {
      setCancellingJobs(prev => [...prev, jobToCancel.job_id]);
      await apiService.cancelJob(jobToCancel.job_id);
      fetchJobs();
      onConfirmModalClose();
      setJobToCancel(null);
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast({ title: 'Failed to cancel job', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setCancellingJobs(prev => prev.filter(id => id !== jobToCancel.job_id));
    }
  };

  const handleViewError = async (job) => {
    setSelectedError({
      jobId: job.job_id,
      error: job.error || 'No error details available',
      jobName: job.job_name || `Job ${job.job_id}`
    });
    
    // Reset diagnosis data and start loading
    setDiagnosisData(null);
    setDiagnosisLoading(true);
    onErrorModalOpen();
    
    // Check subscription status first
    try {
      const purchaseSummary = await apiService.getPurchaseSummary();
      
      if (purchaseSummary?.product_name === "On Demand Usage") {
        // User has On Demand Usage, proceed with AI auto-fix
        try {
          const autoFixResult = await apiService.autoFixJob(job.job_id);
          setDiagnosisData(autoFixResult);
        } catch (error) {
          console.error('Failed to load auto-fix:', error);
          // Auto-fix failed, but we can still show the basic error
        }
      } else {
        // User doesn't have On Demand Usage subscription
        setDiagnosisData({ 
          subscription_required: true,
          product_name: purchaseSummary?.product_name || 'Free Account'
        });
      }
    } catch (error) {
      console.error('Failed to check subscription:', error);
      // If we can't check subscription, assume no access
      setDiagnosisData({ 
        subscription_required: true,
        product_name: 'Unknown'
      });
    } finally {
      setDiagnosisLoading(false);
    }
  };

  // Update the handleViewJobLogs function
  const handleViewJobLogs = async (job) => {
    try {
      setJobLogsLoading(true);
      setSelectedJobLogs({
        jobId: job.job_id,
        logs: '',
        jobName: job.job_name || `Job ${job.job_id}`,
        status: job.status // Add status here
      });
      onJobLogsModalOpen();
      
      // Fetch job logs from the API
      const logs = await apiService.getJobLogs(job.job_id);
      console.log(logs);
      
      // Extract log_content from the response object
      const logContent = logs?.log_content || logs || 'No logs available for this job.';
      
      setSelectedJobLogs(prev => ({
        ...prev,
        logs: logContent
      }));
      
      // Scroll to bottom after logs are loaded
      setTimeout(() => {
        if (logsContainerRef.current) {
          logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
        }
      }, 100); // Small delay to ensure content is rendered
      
    } catch (error) {
      console.error('Failed to fetch job logs:', error);
      setSelectedJobLogs(prev => ({
        ...prev,
        logs: 'Failed to load job logs. Please try again.'
      }));
    } finally {
      setJobLogsLoading(false);
    }
  };

  const handleCopyLogs = async () => {
    // Extract log content - handle both string and object formats
    const logContent = typeof selectedJobLogs.logs === 'object' && selectedJobLogs.logs?.log_content
      ? selectedJobLogs.logs.log_content
      : selectedJobLogs.logs;
    
    if (!logContent || (typeof logContent === 'string' && logContent.trim() === '')) return;
    
    try {
      await navigator.clipboard.writeText(logContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy logs:', err);
      toast({ title: 'Failed to copy logs', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // Update the isJobFinished function with better debugging and type handling
  const isJobFinished = (jobId) => {
    const job = jobs.find(j => String(j.job_id) === String(jobId));
    console.log('Checking job finish status:', {
      jobId,
      foundJob: job,
      status: job?.status,
      isFinished: job && (job.status?.toLowerCase() === 'completed' || job.status?.toLowerCase() === 'failed')
    });
    return job && (job.status?.toLowerCase() === 'completed' || job.status?.toLowerCase() === 'failed');
  };

  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    onDeleteModalOpen();
  };

  const handleConfirmDelete = async () => {
    if (!jobToDelete) return;
    
    try {
      setDeletingJobs(prev => [...prev, jobToDelete.job_id]);
      await apiService.deleteJob(jobToDelete.job_id);
      fetchJobs(); // Refresh the job list
      onDeleteModalClose();
      setJobToDelete(null);
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast({ title: 'Failed to delete job', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setDeletingJobs(prev => prev.filter(id => id !== jobToDelete.job_id));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getUniqueTools = () => {
    const tools = [...new Set(jobs.map(job => job.tool_name))].filter(Boolean);
    return tools;
  };

  const getJobCounts = () => {
    const total = jobs.length;
    const completed = jobs.filter(job => job.status?.toLowerCase() === 'completed').length;
    const running = jobs.filter(job => job.status?.toLowerCase() === 'running').length;
    const preprocessing = jobs.filter(job => job.status?.toLowerCase() === 'preprocessing').length;
    const failed = jobs.filter(job => job.status?.toLowerCase() === 'failed').length;
    const cancelled = jobs.filter(job => job.status?.toLowerCase() === 'cancelled').length;
    const enqueued = jobs.filter(job => job.status?.toLowerCase() === 'enqueue').length;
    const deleted = jobs.filter(job => job.status?.toLowerCase() === 'deleted').length;
    
    return { total, completed, running, preprocessing, failed, cancelled, enqueued, deleted };
  };

  const jobCounts = getJobCounts();

  // Pagination calculations
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleJobsPerPageChange = (newJobsPerPage) => {
    setJobsPerPage(parseInt(newJobsPerPage));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <Box minH="100vh" bg={bg} display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text>Loading your jobs...</Text>
        </VStack>
      </Box>
    );
  }

  const handleCopyFixedCode = async () => {
    if (!diagnosisData?.fixed_code) return;
    try {
      await navigator.clipboard.writeText(diagnosisData.fixed_code);
      setFixedCodeCopySuccess(true);
      setTimeout(() => setFixedCodeCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy fixed code:', err);
      toast({ title: 'Failed to copy code', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // Copilot debug — temporarily hidden
  /*
  const handleDebugWithCopilot = async (job) => {
    try {
      const caseRes = await fetch(`${apiService.baseURL}/job-case/${job.job_id}`, {
        headers: apiService.getHeaders(true),
      });
      const caseData = await caseRes.json();
      const logsData = await apiService.getJobLogs(job.job_id);
      const logs = logsData?.log_content || logsData || '';
      navigate('/copilot', {
        state: {
          debugContext: {
            caseCode: caseData.case_content || '',
            logs: logs,
            jobName: job.job_name || `Job ${job.job_id}`,
            jobId: job.job_id,
            error: job.error || 'No error details available'
          }
        }
      });
    } catch (err) {
      console.error('Failed to load job data for Copilot:', err);
      toast({ title: 'Failed to load job data', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };
  */

  const handleCloseErrorModal = () => {
    onErrorModalClose();
    setDiagnosisLoading(false);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')} 
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              <Button
                leftIcon={<FaCloud />}
                colorScheme="brand"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                Dashboard
              </Button>
              <Button
                leftIcon={<FaSignOutAlt />}
                variant="ghost"
                size="sm"
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Page Header */}
      <Box bg="gray.50" borderBottom="1px" borderColor="gray.200" py={6}>
        <Container maxW="7xl">
          <VStack spacing={4} align="stretch">
            <HStack justifyContent="space-between" alignItems="center">
              <VStack spacing={2} align="start">
                <Heading size="lg" color="brand.600">All Jobs</Heading>
                <Text color="gray.600">Manage and track all your simulation jobs</Text>
              </VStack>

              <HStack spacing={3}>
                <Tooltip
                  label={
                    !jobEligibility || !jobEligibility.is_eligible 
                      ? jobEligibility?.reason || "Quota limit reached"
                      : `${jobEligibility.remaining_hours} hours remaining`
                  }
                  hasArrow
                  isDisabled={!(!jobEligibility || !jobEligibility.is_eligible)}
                  placement="top"
                >

                  <Button                    
                    colorScheme="brand"
                    variant="outline"
                    size="md"
                    onClick={() => navigate('/start-simulation')}
                    isDisabled={!jobEligibility || !jobEligibility.is_eligible}
                    isLoading={jobEligibilityLoading}
                    loadingText="Checking eligibility..."                  
                  >   
                    <Icon as={FaPlay} />
                  </Button>
                </Tooltip>

              {/* Copilot temporarily hidden — functionality still in development
              <Tooltip label="Try the SRS Copilot (Beta)" hasArrow>
              <Button 
                  colorScheme="pink"
                  variant="outline"
                  size="md"
                  minW="auto"
                  px={3}  
                  onClick={() => navigate('/copilot')}
                  position="relative"
                >
                  <Icon as={FaBrain} />
                  <Badge 
                    colorScheme="orange" 
                    variant="solid" 
                    fontSize="6px" 
                    position="absolute" 
                    top="-1" 
                    right="-1" 
                    borderRadius="full"
                    px={1}
                    lineHeight="1.4"
                  >
                    β
                  </Badge>
              </Button>
              </Tooltip>
              */}

              <Tooltip label="Launch Templates" hasArrow>
              <Button 
                  colorScheme="brand"
                  variant="outline"
                  size="md"
                  minW="auto"
                  px={3}  
                  onClick={() => navigate('/examples')}
                >
                  <Icon as={FaRocket} />
              </Button>
              </Tooltip>

              <Tooltip label="Buy more credits to run more simulations" hasArrow>
                <Button 
                    colorScheme="brand"
                    variant="outline"
                    size="md"        
                    minW="auto"
                    px={3}  
                    onClick={() => navigate('/buy-credits')}
                  >
                    <Icon as={FaShoppingCart} />
                </Button>
                </Tooltip>



              <Tooltip label="Manage your account and settings" hasArrow> 
                <Button                
                size="md"
                onClick={() => navigate('/settings')}
                colorScheme="brand"
                variant="outline"
                minW="auto"
                px={3}
                borderRadius="full"
                aria-label="Settings"
              >
                <Icon as={FaCog} boxSize={5} />
              </Button>
              </Tooltip>

                <Tooltip label="Refresh the page" hasArrow> 
                <Button                
                  size="md"
                  onClick={handleRefreshAll}
                  colorScheme="brand"
                  variant="outline"
                  minW="auto"
                  px={3}
                  borderRadius="full"
                  aria-label="Refresh"
                >
                  <FaSyncAlt />
                </Button>
                </Tooltip>
              </HStack>
            </HStack>

            {/* Subscription Details in Page Header */}
            {subscriptionLoading || consumedHoursLoading || purchaseSummaryLoading ? (
              <HStack justify="center" spacing={3}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.600">Loading account...</Text>
              </HStack>
            ) : subscriptionError || consumedHoursError || purchaseSummaryError ? (
              <HStack justify="center" spacing={2}>
                <Text fontSize="sm" color="red.500">
                  {subscriptionError || consumedHoursError || purchaseSummaryError}
                </Text>
                <Button 
                  size="xs" 
                  variant="link" 
                  colorScheme="red" 
                  onClick={() => {
                    if (subscriptionError) fetchSubscriptionDetails();
                    if (consumedHoursError) fetchConsumedHours();
                    if (purchaseSummaryError) fetchPurchaseSummary();
                  }}
                >
                  Retry
                </Button>
              </HStack>
            ) : subscriptionDetails ? (
              <VStack spacing={4} align="stretch">
                {/* Subscription Info */}
                <Box>
                  <Wrap spacing={6} align="center" justify="center">
                                                            
                    
                    {/* Replace Current Period with Purchase Summary */}

                    {purchaseSummary && (
                      <>
                        {purchaseSummary.product_name && (
                          <WrapItem>
                            <HStack spacing={2}>
                              <Text fontSize="sm" color="gray.600">Latest Product:</Text>
                              <Badge 
                                variant="subtle" 
                                colorScheme={purchaseSummary.product_name === 'On Demand Usage' ? 'blue' : purchaseSummary.product_name === 'Free Tier' ? 'yellow' : 'gray'}
                                textTransform="capitalize"
                              >
                                {getProductDisplayName(purchaseSummary.product_name)}
                              </Badge>

                              
                            </HStack>
                          </WrapItem>
                        )}
                        
                        {purchaseSummary.status && (
                          <WrapItem>
                            <HStack spacing={2}>
                              <Text fontSize="sm" color="gray.600">Purchase Status:</Text>
                              <Badge 
                                variant="subtle" 
                                colorScheme={purchaseSummary.status === 'succeeded' ? 'green' : purchaseSummary.status === 'pending' ? 'yellow' : 'gray'}
                                textTransform="capitalize"
                              >
                                {purchaseSummary.status}
                              </Badge>
                            </HStack>
                          </WrapItem>
                        )}
                        

                        <WrapItem>
                          <HStack spacing={2}>
                            <Text fontSize="sm" color="gray.600">Total Purchases:</Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {purchaseSummary.total_purchases}
                            </Text>
                          </HStack>
                        </WrapItem>                        
                        
                        <WrapItem>
                          <HStack spacing={2}>
                            <Text fontSize="sm" color="gray.600">Total Spent:</Text>
                            <Text fontSize="sm" fontWeight="medium">
                              €{(purchaseSummary.total_amount / 100).toFixed(0)}
                            </Text>
                          </HStack>
                        </WrapItem>
                        
                        <WrapItem>
                          <HStack spacing={2}>
                            <Text fontSize="sm" color="gray.600">Last Purchase:</Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {purchaseSummary.last_purchase 
                                ? formatSubscriptionDate(purchaseSummary.last_purchase)
                                : 'Never'
                              }
                            </Text>
                          </HStack>
                        </WrapItem>
                      </>
                    )}
                    
                    <WrapItem>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.600">Member Since:</Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatSubscriptionDate(subscriptionDetails.created_at)}
                        </Text>
                      </HStack>
                    </WrapItem>
                    
                    <WrapItem>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.600">Last Updated:</Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatSubscriptionDate(subscriptionDetails.updated_at)}
                        </Text>
                      </HStack>
                    </WrapItem>
                  </Wrap>
                </Box>

                {/* Usage Statistics */}
                {consumedHours && (
                  <Box>
                    <Divider mb={4} />
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4} templateColumns={{ base: "1fr 1fr", md: "1fr 2fr 1fr" }}>
                      {/* Total Hours Used */}
                      <Box textAlign="center">
                        <HStack spacing={2} justify="center" mb={1}>
                          <Icon as={MdFunctions} color="blue.500" boxSize={6} />
                          <Text fontSize="xs" color="gray.600">Total</Text>
                        </HStack>
                        <Text fontSize="lg" fontWeight="bold" color="blue.600">
                          {formatHours(consumedHours.total_hours)}
                        </Text>
                      </Box>

                      {/* Usage Percentage */}
                      <Box>
                        <VStack spacing={2} align="start">
                          <Box w="full" position="relative">
                            <Box
                              w="full"
                              h="6"
                              bg="gray.200"
                              rounded="full"
                              overflow="visible"
                              shadow="inner"
                              position="relative"
                            >
                              <Box
                                h="full"
                                bg={
                                  consumedHours.percentage_consumed >= 90 
                                    ? "red.500" 
                                    : consumedHours.percentage_consumed >= 75 
                                    ? "orange.500" 
                                    : "green.500"
                                }
                                rounded="md"
                                w={`${Math.min(consumedHours.percentage_consumed || 0, 100)}%`}
                                transition="all 0.5s ease-in-out"
                                position="relative"
                                _after={{
                                  content: '""',
                                  position: 'absolute',
                                  top: '0',
                                  left: '0',
                                  right: '0',
                                  bottom: '0',
                                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                                  borderRadius: 'full'
                                }}
                              />
                              
                              {/* Percentage Pointer */}
                              <Box
                                position="absolute"
                                left={`${Math.min(consumedHours.percentage_consumed || 0, 100)}%`}
                                top="-8px"
                                transform="translateX(-50%)"
                                transition="all 0.5s ease-in-out"
                              >
                                {/* Pointer Arrow */}
                                <Box
                                  w="0"
                                  h="0"
                                  borderLeft="6px solid transparent"
                                  borderRight="6px solid transparent"
                                  borderTop={`8px solid ${
                                    consumedHours.percentage_consumed >= 90 
                                      ? "#E53E3E" 
                                      : consumedHours.percentage_consumed >= 75 
                                      ? "#DD6B20" 
                                      : "#38A169"
                                  }`}
                                  mx="auto"
                                />
                                {/* Percentage Text */}
                                <Box
                                  bg={
                                    consumedHours.percentage_consumed >= 90 
                                      ? "red.500" 
                                      : consumedHours.percentage_consumed >= 75 
                                      ? "orange.500" 
                                      : "green.500"
                                  }
                                  color="white"
                                  px={2}
                                  py={1}
                                  rounded="md"
                                  fontSize="xs"
                                  fontWeight="bold"
                                  whiteSpace="nowrap"
                                  shadow="md"
                                  mt="-1px"
                                >
                                  {consumedHours.percentage_consumed?.toFixed(1)}%
                                </Box>
                              </Box>
                            </Box>
                            
                            <Text 
                              fontSize="2xs" 
                              color="gray.500" 
                              mt={3}
                              textAlign="left"
                            >
                              {formatHours((consumedHours.total_hours-consumedHours.remaining_hours))} / {formatHours(consumedHours.total_hours)}
                            </Text>
                          </Box>
                        </VStack>
                      </Box>

                      {/* Remaining Hours */}
                      <Box textAlign="center">
                        <HStack spacing={2} justify="center" mb={1}>
                          <Icon as={FaHourglassHalf} color="green.500" boxSize={4} />
                          <Text fontSize="xs" color="gray.600">Remaining</Text>
                        </HStack>
                        <Text fontSize="lg" fontWeight="bold" color="green.600">
                          {formatHours(consumedHours.remaining_hours)}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                )}
              </VStack>
            ) : null}
          </VStack>
        </Container>
      </Box>

      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Quick Stats */}
          <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={4}>
            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Total Jobs</StatLabel>
              <StatNumber color="brand.500">{jobCounts.total}</StatNumber>
            </Stat>

            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Enqueued</StatLabel>
              <StatNumber color="yellow.500">{jobCounts.enqueued}</StatNumber>
            </Stat>
            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Preprocessing</StatLabel>
              <StatNumber color="purple.500">{jobCounts.preprocessing}</StatNumber>
            </Stat>
            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Running</StatLabel>
              <StatNumber color="blue.500">{jobCounts.running}</StatNumber>
            </Stat>

            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Completed</StatLabel>
              <StatNumber color="green.500">{jobCounts.completed}</StatNumber>
            </Stat>
            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Cancelled</StatLabel>
              <StatNumber color="gray.500">{jobCounts.cancelled}</StatNumber>
            </Stat>
            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Failed</StatLabel>
              <StatNumber color="red.500">{jobCounts.failed}</StatNumber>
            </Stat>
            <Stat bg={cardBg} p={4} rounded="lg" shadow="sm" textAlign="center">
              <StatLabel fontSize="sm" color="gray.600">Deleted</StatLabel>
              <StatNumber color="red.500">{jobCounts.deleted}</StatNumber>
            </Stat>
          </SimpleGrid>

          {/* Filters */}
          <Card bg={cardBg} shadow="sm">
            <CardBody>
              <VStack spacing={4}>
                <Heading size="md" alignSelf="start">Filter Jobs</Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>Search</Text>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <FaSearch color="gray.300" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search by job name, description, or ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </InputGroup>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>Status</Text>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="all">All Statuses</option>
                      <option value="completed">Completed</option>
                      <option value="running">Running</option>
                      <option value="preprocessing">Preprocessing</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="enqueue">Enqueued</option>
                      <option value="failed">Failed</option>
                      <option value="deleted">Deleted</option>
                    </Select>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>Tool</Text>
                    <Select value={toolFilter} onChange={(e) => setToolFilter(e.target.value)}>
                      <option value="all">All Tools</option>
                      {getUniqueTools().map(tool => (
                        <option key={tool} value={tool}>{tool}</option>
                      ))}
                    </Select>
                  </Box>
                </SimpleGrid>
                <HStack spacing={2} alignSelf="start">
                  <Text fontSize="sm" color="gray.600">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredJobs.length)} of {filteredJobs.length} jobs
                  </Text>
                  {(statusFilter !== 'all' || searchQuery || toolFilter !== 'all') && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setStatusFilter('all');
                        setSearchQuery('');
                        setToolFilter('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Jobs Table */}
          {error ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
              <Button ml={4} size="sm" onClick={fetchJobs}>
                Retry
              </Button>
            </Alert>
          ) : filteredJobs.length === 0 ? (
            <Card bg={cardBg} shadow="sm">
              <CardBody textAlign="center" py={12}>
                <Icon as={FaServer} color="gray.400" boxSize={16} mb={4} />
                <Heading size="lg" color="gray.500" mb={2}>
                  {jobs.length === 0 ? 'No Jobs Found' : 'No Jobs Match Your Filters'}
                </Heading>
                <Text color="gray.600" mb={6}>
                  {jobs.length === 0 
                    ? "You haven't started any simulations yet. Start your first simulation to see it here."
                    : "Try adjusting your filters to see more results."
                  }
                </Text>
                <Button 
                  colorScheme="brand" 
                  leftIcon={<FaPlay />}
                  onClick={() => navigate('/start-simulation')}
                >
                  Start New Simulation
                </Button>
              </CardBody>
            </Card>
          ) : (
            <Card bg={cardBg} shadow="sm">
              <CardBody p={0}>
                <TableContainer>
                  <Table variant="simple" layout="fixed" w="full">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th w="25%">Job Details</Th>
                        <Th w="12%">Status</Th>
                        <Th w="10%">Tool</Th>
                        <Th w="18%">Dates</Th>
                        <Th w="12%">Duration</Th>
                        <Th w="10%">File Size</Th>
                        <Th w="13%">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {currentJobs.map((job) => {
                        const isDownloading = downloadingJobs.includes(job.job_id);
                        const status = job.status?.toLowerCase();
                        
                        return (
                          <Tr key={job.job_id} _hover={{ bg: "gray.50" }}>
                            <Td maxW="0" overflow="hidden">
                              <VStack align="start" spacing={1} w="full">
                                <Tooltip
                                  label={job.job_name || `Job ${job.job_id}`}
                                  placement="top"
                                  hasArrow
                                  bg="gray.800"
                                  color="white"
                                  fontSize="sm"
                                  px={3}
                                  py={2}
                                  borderRadius="md"
                                  maxW="400px"
                                  whiteSpace="normal"
                                  wordBreak="break-word"
                                >
                                  <Text
                                    fontWeight="medium"
                                    noOfLines={1}
                                    w="full"
                                    cursor="help"
                                  >
                                    {job.job_name || `Job ${job.job_id}`}
                                  </Text>
                                </Tooltip>
                                <Tooltip
                                  label={job.job_desc || 'No description available'}
                                  placement="top"
                                  hasArrow
                                  bg="gray.800"
                                  color="white"
                                  fontSize="sm"
                                  px={3}
                                  py={2}
                                  borderRadius="md"
                                  maxW="400px"
                                  whiteSpace="normal"
                                  wordBreak="break-word"
                                  isDisabled={!job.job_desc || job.job_desc.trim() === ''}
                                >
                                  <Text 
                                    fontSize="sm" 
                                    color="gray.600" 
                                    w="full"
                                    whiteSpace="normal"
                                    wordBreak="break-word"
                                    noOfLines={3}
                                    cursor={job.job_desc && job.job_desc.trim() ? "help" : "default"}
                                  >
                                    {job.job_desc || 'No description'}
                                  </Text>
                                </Tooltip>
                                <Text fontSize="xs" color="gray.500" noOfLines={1} w="full">
                                  CODE: {job.job_id}
                                </Text>
                                {job.ray_job_id && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    ID: {job.ray_job_id}
                                  </Text>
                                )}
                                {status !== 'deleted' && <Text
                                  as="button"
                                  fontSize="xs"
                                  color="brand.500"
                                  _hover={{ color: 'brand.600', textDecoration: 'underline' }}
                                  cursor="pointer"
                                  display="inline-flex"
                                  alignItems="center"
                                  gap={1}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const res = await fetch(`${apiService.baseURL}/job-case/${job.job_id}`, {
                                        headers: apiService.getHeaders(true),
                                      });
                                      if (!res.ok) throw new Error('Failed');
                                      const data = await res.json();
                                      if (!data.exists || !data.case_content) {
                                        toast({ title: 'Case file not available', status: 'warning', duration: 3000, isClosable: true });
                                        return;
                                      }
                                      const blob = new Blob([data.case_content], { type: 'text/plain' });
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = 'pyfr.ini';
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      URL.revokeObjectURL(url);
                                    } catch {
                                      toast({ title: 'Failed to download case file', status: 'error', duration: 3000, isClosable: true });
                                    }
                                  }}
                                >
                                  <FaDownload size={10} /> pyfr.ini
                                </Text>}
                              </VStack>
                            </Td>
                            <Td>
                              <Badge 
                                colorScheme={getStatusColor(job.status)} 
                                px={2}
                                py={1}
                                fontSize="xs"
                              >
                                <HStack spacing={1}>
                                  <Icon as={getStatusIcon(job.status)} boxSize={3} />
                                  <Text>{job.status?.toUpperCase() || 'UNKNOWN'}</Text>
                                </HStack>
                              </Badge>
                            </Td>
                            <Td>
                              <Tooltip label={job.tool_name} placement="top">
                                <Text fontSize="sm" noOfLines={2} wordBreak="break-word">
                                  {job.tool_name}
                                </Text>
                              </Tooltip>
                            </Td>
                            <Td>
                              <VStack align="start" spacing={1} w="full">
                                <HStack spacing={1} w="full">
                                  <Icon as={FaCalendarAlt} boxSize={3} color="gray.400" flexShrink={0} />
                                  <Text fontSize="xs" color="gray.600" noOfLines={1}>
                                    {formatDate(job.enqueued_at)}
                                  </Text>
                                </HStack>
                                {job.started_at && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    Started: {formatDate(job.started_at)}
                                  </Text>
                                )}
                                {job.ended_at && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    Ended: {formatDate(job.ended_at)}
                                  </Text>
                                )}
                              </VStack>
                            </Td>
                            <Td>
                              {/* Show estimated time for running/preprocessing jobs */}
                              {(status === 'running' || status === 'preprocessing') ? (
                                <VStack align="start" spacing={1}>
                                  {estimatedTimesLoading[job.job_id] ? (
                                    <HStack spacing={2}>
                                      <Spinner size="xs" color="blue.500" />
                                      <Text fontSize="xs" color="gray.500">Loading...</Text>
                                    </HStack>
                                  ) : estimatedTimes[job.job_id] ? (
                                    <>
                                      {/* Human-readable ETA */}
                                      {estimatedTimes[job.job_id].execution_time && (
                                       <Tooltip 
                                       label="Estimated time until job completion based on current progress, exluding pre- and post-processing stages." 
                                       placement="top"
                                       hasArrow
                                     >
                                       <Text fontSize="sm" color="orange.600" fontWeight="medium">
                                         ETA: {estimatedTimes[job.job_id].execution_time}
                                       </Text>
                                     </Tooltip>
                                      )}
                                      
                                      
                                      {/* Average Time per Step */}
                                      {estimatedTimes[job.job_id].avg_time_per_step && 
                                       estimatedTimes[job.job_id].avg_time_per_step > 0 && (
                                        <Text fontSize="2xs" color="gray.500">
                                          {estimatedTimes[job.job_id].avg_time_per_step.toFixed(3)}s per step
                                          </Text>
                                      )}
                                      
                                      {/* Moving Average Window */}
                                      {estimatedTimes[job.job_id].moving_avg_window && (
                                        <Text fontSize="2xs" color="gray.400">
                                          Window: {estimatedTimes[job.job_id].moving_avg_window} steps
                                        </Text>
                                      )}
                                      
                                      {/* Exit Code indicator */}
                                      {estimatedTimes[job.job_id].exit_code !== undefined && 
                                       estimatedTimes[job.job_id].exit_code !== 0 && (
                                        <Badge colorScheme="yellow" fontSize="2xs">
                                          {estimatedTimes[job.job_id].message}
                                        </Badge>
                                      )}
                                      
                                    </>
                                  ) : (
                                    <Text fontSize="sm" color="blue.500">Running...</Text>
                                  )}
                                </VStack>
                              ) : (
                                /* Show actual duration for completed/failed/cancelled/deleted jobs */
                                <VStack align="start" spacing={1}>
                                  {(status === 'completed' || status === 'failed' || status === 'cancelled' || status === 'deleted') && job.total_duration && (
                                    <Text fontSize="sm" noOfLines={1}>
                                      Total: {formatDuration(job.total_duration)}
                                    </Text>
                                  )}
                                  {(status === 'completed' || status === 'failed' || status === 'cancelled' || status === 'deleted') && 
                                  job.processing_duration !== null && job.processing_duration !== undefined && (
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                      Processing: {formatDuration(job.processing_duration)}
                                    </Text>
                                  )}
                                  {(!job.total_duration && status !== 'completed' && status !== 'failed' && status !== 'running' && status !== 'preprocessing' && status !== 'deleted') && (
                                    <Text fontSize="sm" color="gray.400">-</Text>
                                  )}
                                </VStack>
                              )}
                            </Td>

                            <Td>
                              <VStack align="start" spacing={1}>
                                {job.results_size && (
                                  <HStack spacing={1}>
                                    <Icon as={FaFileAlt} boxSize={3} color="gray.400" />
                                    <Text fontSize="sm" noOfLines={1}>{formatFileSize(job.results_size)}</Text>
                                  </HStack>
                                )}
                                {!job.results_size && (
                                  <Text fontSize="sm" color="gray.400">-</Text>
                                )}
                              </VStack>
                            </Td>
                            <Td>
                              <VStack spacing={2} align="stretch">
                              {status === 'deleted' ? (
                                  <Button
                                    leftIcon={<FaTrash />}
                                    colorScheme="red"
                                    size="sm"
                                    isDisabled
                                  >
                                    Deleted
                                  </Button>
                                ) : status === 'failed' ? (
                                  <>
                                    <Button
                                      leftIcon={<FaExclamationTriangle />}
                                      colorScheme="red"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewError(job)}
                                    >
                                      Error
                                    </Button>
                                    {/* Copilot temporarily hidden — functionality still in development
                                    <Tooltip
                                      label={
                                        copilotBalanceLoading ? 'Checking credits...' :
                                        !copilotBalance?.has_credits ? 'Purchase Copilot credits to debug with AI' :
                                        'Debug this error with SRS Copilot'
                                      }
                                      hasArrow
                                    >
                                      <Button
                                        leftIcon={<FaBrain />}
                                        colorScheme="pink"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDebugWithCopilot(job)}
                                        isDisabled={copilotBalanceLoading || !copilotBalance?.has_credits}
                                        isLoading={copilotBalanceLoading}
                                      >
                                        Copilot
                                      </Button>
                                    </Tooltip>
                                    */}
                                    <Button
                                      leftIcon={<FaFileAlt />}
                                      colorScheme="blue"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewJobLogs(job)}
                                    >
                                      Logs
                                    </Button>
                                    <Button
                                      leftIcon={deletingJobs.includes(job.job_id) ? <Spinner size="xs" /> : <FaTrash />}
                                      colorScheme="red"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteClick(job)}
                                      isLoading={deletingJobs.includes(job.job_id)}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                ) : status === 'completed' ? (
                                  <>
                                    {/* Viewer temporarily disabled — needs further improvements
                                    <Button
                                      leftIcon={<FaFilm />}
                                      colorScheme="green"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/viewer/${job.job_id}`)}
                                    >
                                      Viewer
                                    </Button>
                                    */}
                                    <Button
                                      leftIcon={isDownloading ? <Spinner size="xs" /> : <FaDownload />}
                                      colorScheme="brand"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDownload(job.job_id)}
                                      isLoading={isDownloading}
                                      loadingText="Getting URL..."
                                    >
                                      Download
                                    </Button>
                                    <Button
                                      leftIcon={<FaFileAlt />}
                                      colorScheme="blue"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewJobLogs(job)}
                                    >
                                      Logs
                                    </Button>
                                    <Button
                                      leftIcon={deletingJobs.includes(job.job_id) ? <Spinner size="xs" /> : <FaTrash />}
                                      colorScheme="red"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteClick(job)}
                                      isLoading={deletingJobs.includes(job.job_id)}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                ) : (status === 'enqueue' || status === 'running' || status === 'preprocessing') ? (
                                  <>
                                    <Button
                                      leftIcon={cancellingJobs.includes(job.job_id) ? <Spinner size="xs" /> : <FaBan />}
                                      colorScheme="gray"
                                      variant="none"
                                      size="sm"
                                      onClick={() => handleCancelClick(job)}
                                      isLoading={cancellingJobs.includes(job.job_id)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      leftIcon={<FaFileAlt />}
                                      colorScheme="blue"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewJobLogs(job)}
                                    >
                                      Logs
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      leftIcon={<FaBan />}
                                      colorScheme="gray"
                                      size="sm"
                                      isDisabled
                                    >
                                      {status === 'cancelled' ? 'Cancelled' : 'Pending'}
                                    </Button>
                                    <Button
                                      leftIcon={<FaFileAlt />}
                                      colorScheme="blue"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewJobLogs(job)}
                                    >
                                      Logs
                                    </Button>
                                    {(status === 'cancelled') && (
                                      <Button
                                        leftIcon={deletingJobs.includes(job.job_id) ? <Spinner size="xs" /> : <FaTrash />}
                                        colorScheme="red"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDeleteClick(job)}
                                        isLoading={deletingJobs.includes(job.job_id)}
                                      >
                                        Delete
                                      </Button>
                                    )}
                                  </>
                                )}
                              </VStack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
            </Card>
          )}

          {/* Pagination Controls */}
          {filteredJobs.length > 0 && (
            <Card bg={cardBg} shadow="sm">
              <CardBody>
                <HStack justify="space-between" align="center" flexWrap="wrap" spacing={4}>
                  {/* Items per page selector */}
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">Show:</Text>
                    <Select 
                      size="sm" 
                      w="auto" 
                      value={jobsPerPage} 
                      onChange={(e) => handleJobsPerPageChange(e.target.value)}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Select>
                    <Text fontSize="sm" color="gray.600">jobs per page</Text>
                  </HStack>

                  {/* Page info */}
                  <Text fontSize="sm" color="gray.600">
                    Page {currentPage} of {totalPages} ({filteredJobs.length} total jobs)
                  </Text>

                  {/* Pagination buttons */}
                  <HStack spacing={1}>
                    <IconButton
                      icon={<FaChevronLeft />}
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(currentPage - 1)}
                      isDisabled={currentPage === 1}
                      aria-label="Previous page"
                    />
                    
                    {getPageNumbers().map((pageNum, index) => (
                      <React.Fragment key={index}>
                        {pageNum === '...' ? (
                          <Text px={2} color="gray.400">...</Text>
                        ) : (
                          <Button
                            size="sm"
                            variant={currentPage === pageNum ? "solid" : "outline"}
                            colorScheme={currentPage === pageNum ? "brand" : "gray"}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        )}
                      </React.Fragment>
                    ))}
                    
                    <IconButton
                      icon={<FaChevronRight />}
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(currentPage + 1)}
                      isDisabled={currentPage === totalPages}
                      aria-label="Next page"
                    />
                  </HStack>
                </HStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmModalOpen} onClose={onConfirmModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FaExclamationTriangle} color="orange.500" />
              <Text>Confirm Job Cancellation</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to cancel this job? This action cannot be undone.
              </Text>
              {jobToCancel && (
                <Box bg="gray.50" p={4} rounded="md" border="1px" borderColor="gray.200">
                  <VStack align="start" spacing={2}>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">Job Name:</Text>
                      <Text>{jobToCancel.job_name || `Job ${jobToCancel.job_id}`}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">Job ID:</Text>
                      <Code fontSize="sm">{jobToCancel.job_id}</Code>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">Status:</Text>
                      <Badge colorScheme={getStatusColor(jobToCancel.status)} size="sm">
                        {jobToCancel.status?.toUpperCase()}
                      </Badge>
                    </Box>
                  </VStack>
                </Box>
              )}
              <Alert status="warning" size="sm">
                <AlertIcon />
                <Text fontSize="sm">
                  Cancelling this job will stop all processing and you will not be able to download any results.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onConfirmModalClose}>
                Keep Job Running
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleConfirmCancel}
                isLoading={jobToCancel && cancellingJobs.includes(jobToCancel.job_id)}
                loadingText="Cancelling..."
              >
                Yes, Cancel Job
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Error Modal */}
      <Modal isOpen={isErrorModalOpen} onClose={handleCloseErrorModal} size="6xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FaExclamationTriangle} color="red.500" />
              <Text>Job Error Details & AI Auto-Fix</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontWeight="bold" mb={2}>Job Name:</Text>
                <Text>{selectedError.jobName}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={2}>Job ID:</Text>
                <Code>{selectedError.jobId}</Code>
              </Box>
              <Divider />
              <Box>
                <Text fontWeight="bold" mb={2} color="red.600">Error Details:</Text>
                <Box 
                  bg="red.50" 
                  border="1px" 
                  borderColor="red.200" 
                  p={4} 
                  rounded="md"
                  maxH="200px"
                  overflowY="auto"
                >
                  <Code 
                    display="block" 
                    whiteSpace="pre-wrap" 
                    fontSize="sm"
                    bg="transparent"
                    color="red.800"
                  >
                    {selectedError.error}
                  </Code>
                </Box>
              </Box>

              {/* AI Auto-Fix Section */}
              <Divider />
              <Box>
                <HStack mb={3}>
                  <Icon as={FaBrain} color="blue.500" />
                  <Text fontWeight="bold" color="blue.600">AI Auto-Fix</Text>
                </HStack>
                
                {diagnosisLoading && (
                  <HStack spacing={3} justify="space-between" bg="blue.50" p={4} rounded="md">
                    <HStack spacing={3}>
                      <Spinner size="sm" color="blue.500" />
                      <Text fontSize="sm" color="gray.600">Analyzing error and generating fix...</Text>
                    </HStack>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      colorScheme="red"
                      leftIcon={<FaTimes />}
                      onClick={handleCloseErrorModal}
                    >
                      Cancel
                    </Button>
                  </HStack>
                )}

                {!diagnosisLoading && !diagnosisData && (
                  <Alert status="warning">
                    <AlertIcon />
                    <Text fontSize="sm">AI auto-fix could not be completed for this error.</Text>
                  </Alert>
                )}

                {diagnosisData && diagnosisData.subscription_required && (
                  <Alert status="warning">
                    <AlertIcon />
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm" fontWeight="semibold">
                        AI Auto-Fix Not Available
                      </Text>
                      <Text fontSize="sm">
                        AI auto-fix is not included in free accounts. Please buy some credits from the{' '}
                        <Button
                          variant="link"
                          colorScheme="blue"
                          fontSize="sm"
                          p={0}
                          h="auto"
                          minW="auto"
                          onClick={() => {
                            handleCloseErrorModal();
                            navigate('/buy-credits');
                          }}
                        >
                          buy credits page
                        </Button>
                        {' '}to unlock this feature.
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Current plan: {getProductDisplayName(diagnosisData.product_name)}
                      </Text>
                    </VStack>
                  </Alert>
                )}

                {diagnosisData && !diagnosisData.subscription_required && (
                  <VStack spacing={4} align="stretch">
                    {/* Error Summary */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontWeight="semibold" mb={2} color="gray.600">Error Stage:</Text>
                        <Badge 
                          colorScheme={
                            diagnosisData.error_stage === 'runtime' ? 'red' : 
                            diagnosisData.error_stage === 'pre_process' ? 'orange' : 'yellow'
                          }
                          fontSize="sm"
                          px={3}
                          py={1}
                        >
                          {diagnosisData.error_stage?.toUpperCase()}
                        </Badge>
                      </Box>
                      
                      <Box>
                        <Text fontWeight="semibold" mb={2} color="gray.600">Severity:</Text>
                        <Badge 
                          colorScheme={
                            diagnosisData.severity === 'critical' ? 'red' : 
                            diagnosisData.severity === 'high' ? 'orange' : 
                            diagnosisData.severity === 'moderate' ? 'yellow' : 'green'
                          }
                          fontSize="sm"
                          px={3}
                          py={1}
                        >
                          {diagnosisData.severity?.toUpperCase()}
                        </Badge>
                      </Box>

                      <Box gridColumn={{ base: 1, md: 'span 2' }}>
                        <Text fontWeight="semibold" mb={2} color="gray.600">Error Type:</Text>
                        <Text fontSize="sm" bg="gray.50" p={3} rounded="md" border="1px" borderColor="gray.200">
                          {diagnosisData.error_type}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    {/* Root Cause */}
                    <Box>
                      <Text fontWeight="semibold" mb={2} color="orange.600">Root Cause:</Text>
                      <Text fontSize="sm" bg="orange.50" p={3} rounded="md" border="1px" borderColor="orange.200">
                        {diagnosisData.root_cause}
                      </Text>
                    </Box>

                    {/* Fix Type & Explanation */}
                    <Box>
                      <HStack mb={2} justify="space-between">
                        <Text fontWeight="semibold" color="green.600">Fix Applied:</Text>
                        <Badge colorScheme="green" fontSize="xs">
                          {diagnosisData.fix_type?.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                      </HStack>
                      <Text fontSize="sm" bg="green.50" p={3} rounded="md" border="1px" borderColor="green.200">
                        {diagnosisData.explanation}
                      </Text>
                    </Box>

                    {/* Fixed Code */}
                    {diagnosisData.fixed_code && (
                      <Box>
                        <HStack mb={2} justify="space-between">
                          <Text fontWeight="semibold" color="blue.600">Fixed Code:</Text>
                          <Button
                            size="sm"
                            leftIcon={fixedCodeCopySuccess ? <FaCheckCircle /> : <FaCopy />}
                            colorScheme={fixedCodeCopySuccess ? "green" : "blue"}
                            onClick={handleCopyFixedCode}
                            variant="outline"
                          >
                            {fixedCodeCopySuccess ? "Copied!" : "Copy Code"}
                          </Button>
                        </HStack>
                        <Box 
                          bg="gray.900" 
                          border="1px" 
                          borderColor="gray.700" 
                          p={4} 
                          rounded="md"
                          maxH="400px"
                          overflowY="auto"
                        >
                          <Code 
                            display="block" 
                            whiteSpace="pre-wrap" 
                            fontSize="sm"
                            bg="transparent"
                            color="green.300"
                            fontFamily="monospace"
                          >
                            {diagnosisData.fixed_code}
                          </Code>
                        </Box>
                      </Box>
                    )}

                    {/* Validation Summary */}
                    {diagnosisData.validation_summary && (
                      <Box>
                        <Text fontWeight="semibold" mb={3} color="purple.600">Validation Summary:</Text>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                          {Object.entries(diagnosisData.validation_summary).map(([key, value]) => (
                            <HStack key={key} spacing={2} bg="gray.50" p={2} rounded="md">
                              <Icon 
                                as={value ? FaCheckCircle : FaTimes} 
                                color={value ? "green.500" : "red.500"} 
                              />
                              <Text fontSize="sm" flex={1}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </Text>
                              <Badge colorScheme={value ? "green" : "red"} size="sm">
                                {value ? "PASS" : "FAIL"}
                              </Badge>
                            </HStack>
                          ))}
                        </SimpleGrid>
                      </Box>
                    )}

                    {/* Optional Recommendations */}
                    {diagnosisData.optional_recommendations && Object.keys(diagnosisData.optional_recommendations).length > 0 && (
                      <Box>
                        <Text fontWeight="semibold" mb={2} color="blue.600">Recommendations:</Text>
                        <VStack align="stretch" spacing={2} bg="blue.50" p={3} rounded="md" border="1px" borderColor="blue.200">
                          {diagnosisData.optional_recommendations.suggested_next_test_case && (
                            <Box>
                              <Text fontSize="xs" fontWeight="semibold" color="blue.700">Next Test Case:</Text>
                              <Text fontSize="sm">{diagnosisData.optional_recommendations.suggested_next_test_case}</Text>
                            </Box>
                          )}
                          {diagnosisData.optional_recommendations.stability_margin_comment && (
                            <Box>
                              <Text fontSize="xs" fontWeight="semibold" color="blue.700">Stability:</Text>
                              <Text fontSize="sm">{diagnosisData.optional_recommendations.stability_margin_comment}</Text>
                            </Box>
                          )}
                          {diagnosisData.optional_recommendations.changed_lines_summary && (
                            <Box>
                              <Text fontSize="xs" fontWeight="semibold" color="blue.700">Changes:</Text>
                              <Text fontSize="sm" whiteSpace="pre-wrap">
                                {typeof diagnosisData.optional_recommendations.changed_lines_summary === 'string' 
                                  ? diagnosisData.optional_recommendations.changed_lines_summary 
                                  : diagnosisData.optional_recommendations.changed_lines_summary.join('\n')}
                              </Text>
                            </Box>
                          )}
                        </VStack>
                      </Box>
                    )}

                    {/* Disclaimer */}
                    <Alert status="info" fontSize="xs">
                      <AlertIcon boxSize={3} />
                      <Box>
                        <Text fontSize="xs" color="gray.700">
                          <Text as="span" fontWeight="semibold">Disclaimer:</Text> The auto-fix is generated using AI and may not account for all physical, numerical, or boundary conditions in your specific setup. Please review and test the fixed code thoroughly before using it in production runs.
                        </Text>
                      </Box>
                    </Alert>
                  </VStack>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleCloseErrorModal}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Job Logs Modal */}
      <Modal isOpen={isJobLogsModalOpen} onClose={onJobLogsModalClose} size="xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FaFileAlt} color="blue.500" />
              <Text>Job Logs</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="bold" mb={2}>Job Name:</Text>
                <Text>{selectedJobLogs.jobName}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={2}>Job ID:</Text>
                <Code>{selectedJobLogs.jobId}</Code>
              </Box>
              <Divider />
              <Box>
                <Text fontWeight="bold" mb={2} color="blue.600">Job Logs:</Text>
                <Box 
                  ref={logsContainerRef}
                  bg="gray.50" 
                  border="1px" 
                  borderColor="gray.200" 
                  p={4} 
                  rounded="md"
                  maxH="400px"
                  overflowY="auto"
                >
                  {jobLogsLoading ? (
                    <VStack spacing={2}>
                      <Spinner color="blue.500" />
                      <Text fontSize="sm" color="gray.600">Loading logs...</Text>
                    </VStack>
                  ) : (() => {
                    // Extract log content - handle both string and object formats
                    let logContent = selectedJobLogs.logs;
                    
                    // If logs is an object with log_content property, extract it
                    if (typeof logContent === 'object' && logContent !== null && 'log_content' in logContent) {
                      logContent = logContent.log_content;
                    }
                    
                    // Check if empty (including empty strings)
                    const isEmpty = !logContent || 
                      (typeof logContent === 'string' && logContent.trim() === '') || 
                      logContent === 'No logs available for this job.';
                    
                    // If still an object at this point (unexpected), stringify it as last resort
                    if (!isEmpty && typeof logContent === 'object' && logContent !== null) {
                      logContent = JSON.stringify(logContent, null, 2);
                    }
                    
                    return isEmpty ? (
                      <VStack spacing={3} py={8}>
                        <Icon as={FaFileAlt} boxSize={12} color="gray.300" />
                        <Text fontSize="md" color="gray.500" fontWeight="medium">
                          No logs available
                        </Text>
                        <Text fontSize="sm" color="gray.400" textAlign="center">
                          {selectedJobLogs.status?.toLowerCase() === 'enqueue' 
                            ? 'Job is queued and hasn\'t started yet.'
                            : selectedJobLogs.status?.toLowerCase() === 'preprocessing'
                            ? 'Job is in preprocessing stage. Logs will appear soon.'
                            : selectedJobLogs.status?.toLowerCase() === 'running'
                            ? 'Job is running. Logs are being generated.'
                            : 'No log output has been generated for this job.'}
                        </Text>
                      </VStack>
                    ) : (
                      <Code 
                        display="block" 
                        whiteSpace="pre-wrap" 
                        fontSize="sm"
                        bg="transparent"
                        color="gray.800"
                        fontFamily="mono"
                      >
                        {logContent}
                      </Code>
                    );
                  })()}
                </Box>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
          {(selectedJobLogs.status?.toLowerCase() === 'completed' || selectedJobLogs.status?.toLowerCase() === 'failed' || selectedJobLogs.status?.toLowerCase() === 'cancelled') ? (
              <Button
                leftIcon={copySuccess ? <FaCheckCircle /> : <Box ml={3}><FaCopy /></Box>}
                colorScheme={copySuccess ? "green" : "blue"}
                onClick={handleCopyLogs}
                isDisabled={jobLogsLoading || (() => {
                  const logContent = typeof selectedJobLogs.logs === 'object' && selectedJobLogs.logs?.log_content
                    ? selectedJobLogs.logs.log_content
                    : selectedJobLogs.logs;
                  return !logContent || (typeof logContent === 'string' && logContent.trim() === '');
                })()}
              >
                {copySuccess ? "Copied!" : ""}
              </Button>
            ) : (
              <Button
                leftIcon={
                  <Box ml={3}>
                    <FaSyncAlt />
                  </Box>
                }
                colorScheme="blue"
                onClick={() => handleViewJobLogs({ 
                  job_id: selectedJobLogs.jobId, 
                  job_name: selectedJobLogs.jobName,
                  status: selectedJobLogs.status
                })}
                isLoading={jobLogsLoading}
                loadingText="Refreshing..."
              >
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Job Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaExclamationTriangle} color="red.500" boxSize={6} />
              <Text>Delete Job</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="start">
              <Text color="gray.700">
                Are you sure you want to permanently delete this job?
              </Text>
              
              {jobToDelete && (
                <Box bg="gray.50" p={4} rounded="md" border="1px" borderColor="gray.200" w="full">
                  <VStack align="start" spacing={2}>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">Job Name:</Text>
                      <Text>{jobToDelete.job_name || `Job ${jobToDelete.job_id}`}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">Job ID:</Text>
                      <Code fontSize="sm">{jobToDelete.job_id}</Code>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">Status:</Text>
                      <Badge colorScheme={getStatusColor(jobToDelete.status)} size="sm">
                        {jobToDelete.status?.toUpperCase()}
                      </Badge>
                    </Box>
                  </VStack>
                </Box>
              )}
              
              <Alert status="error" size="sm">
                <AlertIcon />
                <Box>
                  <Text fontSize="sm" fontWeight="semibold">
                    This action cannot be undone!
                  </Text>
                  <Text fontSize="sm">
                    All job data, results, and logs will be permanently deleted.
                  </Text>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onDeleteModalClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleConfirmDelete}
                isLoading={jobToDelete && deletingJobs.includes(jobToDelete.job_id)}
                loadingText="Deleting..."
              >
                Yes, Delete Job
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Footer />
    </Box>
  );
};

export default Jobs;
